/**
 * Data layer for `auto-pause-campaign-when-threshold-hit`. Typed wrappers over
 * /api/v1/auto-pause-campaign-when-threshold-hit.
 *
 * The BE is a decision + ledger endpoint: POST evaluates (observed vs limit),
 * persists a row when action != 'skip', and returns the stored decision.
 * GET / lists the recent ledger. GET /summary returns per-action counts.
 * Raw fetch() is confined to this file per the D6 dash contract.
 */

export type ThresholdType =
	| "spend"
	| "cap"
	| "conversions"
	| "ftd_count"
	| "clicks"
	| "custom";

export type PauseAction = "pause" | "warn" | "skip";
export type Severity = "low" | "med" | "high" | "critical";

export interface AutoPauseRow {
	id: number;
	tenant_id: string;
	event_id: string;
	campaign_id: string;
	threshold_type: ThresholdType;
	threshold_limit: number;
	observed_value: number;
	action: Exclude<PauseAction, "skip">;
	severity: Severity;
	reason: string;
	status: string;
	created_at: number;
}

export interface ListResponse {
	items: AutoPauseRow[];
}

export interface SummaryBucket {
	action: Exclude<PauseAction, "skip">;
	count: number;
}

export interface SummaryResponse {
	by_action: SummaryBucket[];
	total: number;
}

export interface EvaluateBody {
	event_id: string;
	campaign_id: string;
	threshold_type: ThresholdType;
	threshold_limit: number;
	observed_value?: number;
	action?: PauseAction;
	severity?: Severity;
	reason?: string;
}

export interface SkipDecision {
	ok: true;
	skipped: true;
	action: "skip";
	tenant_id: string;
	event_id: string;
	campaign_id: string;
	threshold_type: ThresholdType;
	threshold_limit: number;
	observed_value: number;
	utilization: number;
	severity: Severity;
}

export interface PersistedDecision {
	ok: true;
	skipped: false;
	id: number;
	tenant_id: string;
	event_id: string;
	campaign_id: string;
	threshold_type: ThresholdType;
	threshold_limit: number;
	observed_value: number;
	utilization: number;
	action: Exclude<PauseAction, "skip">;
	severity: Severity;
	reason: string;
	status: string;
	created_at: number;
}

export type EvaluateResponse = SkipDecision | PersistedDecision;

const BASE = "/api/v1/auto-pause-campaign-when-threshold-hit";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listPauses(params?: {
	campaign_id?: string;
	action?: Exclude<PauseAction, "skip">;
}): Promise<ListResponse> {
	const qs = new URLSearchParams();
	if (params?.campaign_id) qs.set("campaign_id", params.campaign_id);
	if (params?.action) qs.set("action", params.action);
	const url = qs.toString() ? `${BASE}?${qs.toString()}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListResponse>(res);
}

export async function getSummary(): Promise<SummaryResponse> {
	const res = await fetch(`${BASE}/summary`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<SummaryResponse>(res);
}

export async function evaluate(body: EvaluateBody): Promise<EvaluateResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<EvaluateResponse>(res);
}

export const autoPauseQueryKeys = {
	all: ["auto-pause-campaign-when-threshold-hit"] as const,
	list: (params?: { campaign_id?: string; action?: Exclude<PauseAction, "skip"> }) =>
		[
			...autoPauseQueryKeys.all,
			"list",
			params?.campaign_id ?? "",
			params?.action ?? "",
		] as const,
	summary: () => [...autoPauseQueryKeys.all, "summary"] as const,
};

// ---------- Pure client-side utilization compare (mirrors BE decidePause) ----------

/**
 * Utilization ratio = observed / max(limit, 0). Limit<=0 means "no ceiling
 * configured" → returns 0, so the UI never shows a fake breach. Mirrors the
 * clamp in the BE so a client-side preview matches the server decision.
 */
export function utilization(observed: number, limit: number): number {
	const o = Number.isFinite(observed) ? Math.max(0, observed) : 0;
	const l = Number.isFinite(limit) ? limit : 0;
	return l > 0 ? o / l : 0;
}

/**
 * Predicted action for (observed, limit) BEFORE the BE decides — used by the
 * rule list to colour-code "currently breaching?" chips. Matches the BE
 * thresholds: >=1.00 pause, >=0.75 warn, else skip.
 */
export function predictedAction(
	observed: number,
	limit: number,
): PauseAction {
	const u = utilization(observed, limit);
	if (u >= 1.0) return "pause";
	if (u >= 0.75) return "warn";
	return "skip";
}

/**
 * Predicted severity tier (mirrors decidePause severity ladder).
 */
export function predictedSeverity(
	observed: number,
	limit: number,
): Severity {
	const u = utilization(observed, limit);
	if (u >= 1.0) return "critical";
	if (u >= 0.9) return "high";
	if (u >= 0.75) return "med";
	return "low";
}
